import { ReactNode, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CallIntelQueries } from '@frontend/api-call-intel';
import { PersonTypes } from '@frontend/api-person';
import { useAppScopeStore } from '@frontend/scope';
import { contextFactory } from '@frontend/design-system';

dayjs.extend(utc);

// 1) PersonInfo context
export type PersonInfoStore = {
  personInfo: PersonTypes.Person;
};

const [PersonInfoContext, usePersonInfoContext] = contextFactory<PersonInfoStore>(
  'usePersonInfoContext must be used within a PersonInfoStoreProvider'
);

export const PersonInfoStoreProvider = ({
  children,
  personInfo,
}: {
  children: ReactNode;
  personInfo: PersonTypes.Person;
}) => {
  const value = useMemo(() => ({ personInfo }), [personInfo]);
  return <PersonInfoContext.Provider value={value}>{children}</PersonInfoContext.Provider>;
};

export { usePersonInfoContext };

// 2) PatientCallTrends context
export type TrendsContextValue<T extends string> = {
  activeTab: T;
  isOpen: boolean;
  isLoading: boolean;
  setActiveTab: (tab: T) => void;
  toggleOpen: () => void;
};

const [TrendsContext, useTrendsContext] = contextFactory<TrendsContextValue<any>>(
  'PatientCallTrends must be rendered inside a <PatientCallTrends> provider'
);

export const TrendsProvider = <T extends string>({
  children,
  value,
}: {
  children: ReactNode;
  value: TrendsContextValue<T>;
}) => {
  return <TrendsContext.Provider value={value}>{children}</TrendsContext.Provider>;
};

export { useTrendsContext };

// 3) CallIntelPatientInsights context

export type CallInsightHistoryFilterType = 'sentiment' | 'category';

export type CallInsightFilter = {
  filterType: CallInsightHistoryFilterType;
  key: string;
  label: string;
};

export type PatientCallTrendsTabType = 'sentiment-tab' | 'category-tab';

export interface CallIntelPatientInsightsStore {
  /**
   * The ID of the currently selected Patient Trends tab.
   */
  activeTab: PatientCallTrendsTabType;

  /**
   * Switch to a different Patient Trends tab.
   * @param tab the tab ID to select
   */
  setActiveTab: (tab: PatientCallTrendsTabType) => void;

  /**
   * Whether the Patient Trends panel is currently expanded.
   */
  showTrends: boolean;

  /**
   * Open or close the Patient Trends panel.
   * @param open `true` to show, `false` to hide
   */
  setShowTrends: (open: boolean) => void;

  /**
   * The date range for patient call trends.
   * - `start`: The start date is set to 12 months ago from the current date.
   * - `end`: The end date is set to the current date.
   * - Both dates are formatted as `YYYY-MM-DD HH:mm:ss.SSS`.
   */
  dateRange: { start: string; end: string };
}

const [CallIntelPatientInsightsContext, useCallIntelPatientInsights] = contextFactory<CallIntelPatientInsightsStore>(
  'useCallIntelPatientInsights must be used inside its Provider'
);

export const CallIntelPatientInsightsProvider = ({ children, personId }: { children: ReactNode; personId: string }) => {
  const { selectedLocationIds } = useAppScopeStore();

  const [activeTab, setActiveTab] = useState<PatientCallTrendsTabType>('sentiment-tab');
  const [showTrends, setShowTrends] = useState(false);

  const { removeQuery } = CallIntelQueries.useRemoveRecentCallQuery({
    locations: selectedLocationIds,
    personId,
  });

  const dateRange = useMemo(() => {
    const endUtc = dayjs().utc();
    const startUtc = endUtc.subtract(1, 'year'); // start = 12 months ago always

    return {
      start: startUtc.format('YYYY-MM-DD HH:mm:ss.SSS'),
      end: endUtc.format('YYYY-MM-DD HH:mm:ss.SSS'),
    };
  }, []);

  const value = useMemo(
    () => ({
      activeTab,
      setActiveTab,
      showTrends,
      setShowTrends,
      dateRange,
    }),
    [activeTab, showTrends, dateRange]
  );

  useEffect(() => {
    return () => {
      removeQuery();
    };
  }, []);
  return <CallIntelPatientInsightsContext.Provider value={value}>{children}</CallIntelPatientInsightsContext.Provider>;
};

export { useCallIntelPatientInsights };
